import type * as React from "react";
import { useState } from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// UI
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, CirclePlus, Trash2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { FileUpload } from "@/components/FileUpload";
import { FilePreviewModal } from "@/components/FilePreviewModal";

// Services and Utils
import { postEquity } from "@/services/finance";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner";

// helpers
import { useDocumentByType, DOC_TYPES } from "@/helpers/documentTypePreview";
import { S3_BUCKET_LINK } from "@/components/constants";

// ------------------ Types ------------------
type EquityRow = {
  name: string;
  remark: string;
  registeredCapital: number | string;
  storage: number | string;
  expansion: number | string;
  accProfitLoss: number | string;
  other: number | string;
};

type CreateEquityRequest = {
  companyId: string;
  yearly: number;
  docFile: string;
  equityDetails: Array<{
    name: string;
    remark: string;
    registeredCapital: number;
    accProfitLoss: number;
    reserve: number;
    other: number;
    isCurrentYear: boolean;
    expansion: number;
    storage: number;
  }>;
};

// ------------------ Helper Functions ------------------

/**
 * Format the user-visible string with commas.
 * If wrapped in parentheses => negative => re-wrap after inserting commas.
 */
function formatNumberWithCommas(value: string | number) {
  if (!value && value !== 0) return "";

  const stringValue = String(value || "");
  const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
  const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

  // Handle the case when user is typing a decimal point
  if (numericValue.endsWith(".")) {
    return isNegative ? `(${numericValue})` : numericValue;
  }

  // Split number into integer and decimal parts
  const [integerPart, decimalPart] = numericValue.split(".");

  // Format integer part with commas
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Handle decimal part - limit to 2 decimal places
  const formattedDecimal = decimalPart ? decimalPart.slice(0, 2) : undefined;

  // Combine with decimal part if it exists
  const formattedValue =
    formattedDecimal !== undefined
      ? `${formattedInteger}.${formattedDecimal}`
      : formattedInteger;

  return isNegative ? `(${formattedValue})` : formattedValue;
}

/**
 * Parse a string with optional parentheses into a real JS number.
 * E.g. "(5000)" => -5000, "5000" => 5000.
 * Also remove any commas before parsing.
 */
function parseParenthesisNumber(strValue: string | number) {
  if (!strValue) return 0;
  const stringValue = String(strValue).replace(/,/g, ""); // remove commas

  const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
  const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

  const parsed = Number(numericValue) || 0;
  return isNegative ? -parsed : parsed;
}

/**
 * A small helper to handle user keystrokes for a numeric field
 * that may contain parentheses.
 */
function handleNumericChange({
  e,
  index,
  fieldName,
  data,
  setData,
}: {
  e: React.ChangeEvent<HTMLInputElement>;
  index: number;
  fieldName:
    | "registeredCapital"
    | "other"
    | "accProfitLoss"
    | "storage"
    | "expansion";
  data: EquityRow[];
  setData: React.Dispatch<React.SetStateAction<EquityRow[]>>;
}) {
  let value = e.target.value;

  // Allow empty input
  if (!value) {
    const newData = [...data];
    newData[index][fieldName] = "";
    setData(newData);
    return;
  }

  // Remove existing commas
  value = value.replace(/,/g, "");

  // Allow only valid characters (numbers, decimal point, parentheses)
  if (!/^[(]?\d*\.?\d*[)]?$/.test(value) && value !== "") {
    return;
  }

  // Handle decimal places
  if (value.includes(".")) {
    const [integerPart, decimalPart] = value.split(".");
    if (decimalPart && decimalPart.replace(")", "").length > 2) {
      const limitedDecimal = decimalPart.replace(")", "").slice(0, 2);
      value = `${integerPart}.${limitedDecimal}${
        value.endsWith(")") ? ")" : ""
      }`;
    }
  }

  const newData = [...data];
  newData[index][fieldName] = value;
  setData(newData);
}

/**
 * Compute the sum of the row's numeric fields by parsing parentheses-based strings.
 */
function computeRowTotal(row: EquityRow) {
  const cap = parseParenthesisNumber(row.registeredCapital);
  const str = parseParenthesisNumber(row.storage);
  const exp = parseParenthesisNumber(row.expansion);
  const acc = parseParenthesisNumber(row.accProfitLoss);
  const oth = parseParenthesisNumber(row.other);

  const total = cap + str + exp + acc + oth;
  return total.toLocaleString();
}

// ------------------- Main Component -------------------
export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/owner-equity/add/"
)({
  component: AddOwnerEquity,
});
function AddOwnerEquity() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const companyId = localStorage.getItem("companyId");
  const [year, setYear] = useState<number | undefined>(() => {
    const storedYear = localStorage.getItem("year");
    if (!storedYear) return undefined;
    const parsed = Number(storedYear);
    return isNaN(parsed) ? undefined : parsed;
  });

  const [docFile, setDocFile] = useState("");
  const { getDocument } = useDocumentByType();
  const doc = getDocument(DOC_TYPES.OWNER_EQUITY_CHANGE_REPORT);

  // ------------- State for "previous year" rows -------------
  const [initialNotCurrentYearEquity, setInitialNotCurrentYearEquity] =
    useState<EquityRow[]>([
      {
        name: `I. ຍອດເຫຼຶອ 1 ມັງກອນ ${year ? year - 1 : ""}`,
        remark: "",
        registeredCapital: 0,
        storage: 0,
        expansion: 0,
        accProfitLoss: 0,
        other: 0,
      },
      {
        name: "ການປ່ຽນແປງວິທີການບັນຊີ",
        remark: "",
        registeredCapital: 0,
        storage: 0,
        expansion: 0,
        accProfitLoss: 0,
        other: 0,
      },
      {
        name: "ການດັດແກ້ຂໍ້ຜິດພາດ ບ້ວງປີທີ່ຜ່ານມາ",
        remark: "",
        registeredCapital: 0,
        storage: 0,
        expansion: 0,
        accProfitLoss: 0,
        other: 0,
      },
    ]);

  // ------------- State for "current year" rows -------------
  const [initialCurrentYearEquity, setInitialCurrentYearEquity] = useState<
    EquityRow[]
  >([
    {
      name: "ອອກຮຸ້ນ/ເພີ່ມທຶນ",
      remark: "",
      registeredCapital: 0,
      storage: 0,
      expansion: 0,
      accProfitLoss: 0,
      other: 0,
    },
    {
      name: "ຖອນທຶນຄຶນ",
      remark: "",
      registeredCapital: 0,
      storage: 0,
      expansion: 0,
      accProfitLoss: 0,
      other: 0,
    },
    {
      name: "ຈ່າຍເງິນປັນຜົນ",
      remark: "",
      registeredCapital: 0,
      storage: 0,
      expansion: 0,
      accProfitLoss: 0,
      other: 0,
    },
    {
      name: "ກຳໄລ/ຂາດທຶນໃນປີ",
      remark: "",
      registeredCapital: 0,
      storage: 0,
      expansion: 0,
      accProfitLoss: 0,
      other: 0,
    },
    {
      name: "ໂອນກຳໄລເຂົ້າຄັງສະສົມຕາມລະບຽບການ",
      remark: "",
      registeredCapital: 0,
      storage: 0,
      expansion: 0,
      accProfitLoss: 0,
      other: 0,
    },
    {
      name: "ໂອນກຳໄລເຂົ້າຄັງຂະຫຍາຍການຜະລິດ",
      remark: "",
      registeredCapital: 0,
      storage: 0,
      expansion: 0,
      accProfitLoss: 0,
      other: 0,
    },
  ]);

  // ------------------- React Query Mutation -------------------
  const mutation = useMutation({
    mutationFn: (data: CreateEquityRequest) => postEquity(data),
    onSuccess: () => {
      toast({
        title: "ບັນທຶກຂໍ້ມູນສຳເລັດ",
        description: "ບັນທຶກຂໍ້ມູນສຳເລັດແລ້ວ",
        variant: "success",
      });

      queryClient.invalidateQueries({ queryKey: ["equities"] });
      localStorage.setItem("year", JSON.stringify(year));
      navigate({ to: "/report/finance/company/owner-equity" });
    },
    onError: (error: Error) => {
      console.log(error, "Error");

      toast({
        title: "ບໍ່ສາມາດບັນທຶກຂໍ້ມູນໄດ້",
        description: "ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
        variant: "destructive",
      });
    },
  });

  // ------------------ Handlers ------------------
  /**
   * Add a new row with default numeric fields = "0"
   */
  const addNewEquityRow = (
    setEquityDetails: React.Dispatch<React.SetStateAction<EquityRow[]>>,
    prevData: EquityRow[]
  ) => {
    setEquityDetails([
      ...prevData,
      {
        name: "",
        remark: "",
        registeredCapital: "0",
        accProfitLoss: "0",
        storage: "0",
        expansion: "0",
        other: "0",
      },
    ]);
  };

  /**
   * Remove row from state by index
   */
  const removeEquityRow = (
    indexToRemove: number,
    setEquityDetails: React.Dispatch<React.SetStateAction<EquityRow[]>>,
    prevData: EquityRow[]
  ) => {
    setEquityDetails(prevData.filter((_row, idx) => idx !== indexToRemove));
  };

  /**
   * When submitting to backend, parse all parentheses-wrapped values as negative numbers.
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyId) {
      toast({
        title: "ຂໍ້ມູນບໍ່ຄົບຖ້ວນ",
        description: "ກະລຸນາປ້ອນປີ",
        variant: "destructive",
      });
      return;
    }

    if (!docFile) {
      toast({
        title: "ບັນທຶກລົ້ມເຫຼວ",
        description: "ກະລຸນາອັບໂຫຼດເອກະສານຕິດຂັດ",
        variant: "destructive",
      });
      return;
    }

    // Convert each row's numeric strings => real numbers
    const finalNotCurrent = initialNotCurrentYearEquity.map((row) => ({
      ...row,
      registeredCapital: parseParenthesisNumber(row.registeredCapital),
      accProfitLoss: parseParenthesisNumber(row.accProfitLoss),
      reserve: 0,
      other: parseParenthesisNumber(row.other),
      isCurrentYear: false,
      expansion: parseParenthesisNumber(row.expansion),
      storage: parseParenthesisNumber(row.storage),
    }));

    const finalCurrent = initialCurrentYearEquity.map((row) => ({
      ...row,
      registeredCapital: parseParenthesisNumber(row.registeredCapital),
      accProfitLoss: parseParenthesisNumber(row.accProfitLoss),
      reserve: 0,
      other: parseParenthesisNumber(row.other),
      isCurrentYear: true,
      expansion: parseParenthesisNumber(row.expansion),
      storage: parseParenthesisNumber(row.storage),
    })); // Example data shape—adjust if needed
    const data = {
      companyId,
      yearly: year!,
      docFile,
      // equityDetails: {
      // 	// If your backend expects them separate or combined, adjust accordingly
      // 	notCurrentYear: finalNotCurrent,
      // 	currentYear: finalCurrent,
      // },
      equityDetails: [...finalNotCurrent, ...finalCurrent],
    };

    console.log(data, "Data");

    mutation.mutate(data);
  };

  function computeSumForField(data: EquityRow[], fieldName: keyof EquityRow) {
    const sum = data.reduce((acc, row) => {
      return acc + parseParenthesisNumber(row[fieldName]);
    }, 0);
    return sum.toLocaleString();
  }

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
                search: { page: 1, startYear: 0, endYear: 0 },
              })
            }
            className="flex items-center gap-[4px] mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <div className="flex justify-between">
            <div>
              <CardTitle>ເພີ່ມຂໍ້ມູນທຶນເຈົ້າຂອງ</CardTitle>
              <CardDescription>ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບຖ້ວນ</CardDescription>
            </div>
            <FilePreviewModal
              fileUrl={`${S3_BUCKET_LINK}${doc?.documentFile}`}
              fileName={doc?.documentName ?? ""}
              text={doc?.documentName ?? ""}
              description={doc?.description}
              showDownloadButton={true}
              useGoogleViewer={true}
            />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            {/* ------------------- Year Input ------------------- */}

            <div className="flex flex-col gap-2 mb-4">
              <Label htmlFor="yearly">
                ປີ <span className="text-red-500">*</span>
              </Label>
              <Select
                value={year ? String(year) : ""}
                onValueChange={(value) => {
                  setYear(Number(value));
                }}
                required
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="ກະລຸນາເລືອກປີ" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from(
                    { length: new Date().getFullYear() - 1974 },
                    (_, i) => (
                      <SelectItem
                        key={i}
                        value={(new Date().getFullYear() - i).toString()}
                      >
                        {new Date().getFullYear() - i}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* ------------- Equity Table (Not Current Year) ------------- */}
            <Table className="overflow-x-auto w-full border table-fixed">
              <TableHeader>
                <TableRow className="[&>*]:border [&>*]:w-[180px]   [&>*]:font-bold [&>*]:text-black">
                  <TableHead style={{ width: 220 }}>ເນຶ້ອໃນລາຍງານ</TableHead>
                  <TableHead>ທຶນຈົດທະບຽນ</TableHead>
                  <TableHead>ຄັງສະສົມຕາມລະບຽບການ</TableHead>
                  <TableHead>ຄັງຂະຫຍາຍການຜະລິດ</TableHead>
                  <TableHead>ກຳໄລ-ຂາດທຶນສະສົມ</TableHead>
                  <TableHead>ອື່ນ</TableHead>
                  <TableHead>ລວມທຶນ</TableHead>
                  <TableHead>ໝາຍເຫດ</TableHead>
                  <TableHead style={{ width: 100 }}>ຈັດການ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="">
                {initialNotCurrentYearEquity?.map((equity, index) => (
                  <TableRow
                    key={index}
                    className={cn(index === 0 ? "bg-[#216C4C]/20" : "")}
                  >
                    {/* ------ Name ------ */}
                    <TableCell className="border">
                      {/* First 3 rows are “fixed name,” else an input */}
                      {[0, 1, 2].includes(index) ? (
                        <>{equity.name}</>
                      ) : (
                        <Input
                          type="text"
                          value={equity.name}
                          onChange={(e) => {
                            const newRows = [...initialNotCurrentYearEquity];
                            newRows[index].name = e.target.value;
                            setInitialNotCurrentYearEquity(newRows);
                          }}
                          required
                        />
                      )}
                    </TableCell>

                    {/* ------ registeredCapital ------ */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(equity.registeredCapital)}
                        onChange={(e) =>
                          handleNumericChange({
                            e,
                            index,
                            fieldName: "registeredCapital",
                            data: initialNotCurrentYearEquity,
                            setData: setInitialNotCurrentYearEquity,
                          })
                        }
                        inputMode="numeric"
                        className="text-right"
                        placeholder="1,000,000"
                        // required
                      />
                    </TableCell>

                    {/* ------ storage ------ */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(equity.storage)}
                        onChange={(e) =>
                          handleNumericChange({
                            e,
                            index,
                            fieldName: "storage",
                            data: initialNotCurrentYearEquity,
                            setData: setInitialNotCurrentYearEquity,
                          })
                        }
                        inputMode="numeric"
                        // required
                        className="text-right"
                        placeholder="1,000,000"
                      />
                    </TableCell>

                    {/* ------ expansion ------ */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(equity.expansion)}
                        onChange={(e) =>
                          handleNumericChange({
                            e,
                            index,
                            fieldName: "expansion",
                            data: initialNotCurrentYearEquity,
                            setData: setInitialNotCurrentYearEquity,
                          })
                        }
                        inputMode="numeric"
                        className="text-right"
                        placeholder="1,000,000"
                        // required
                      />
                    </TableCell>

                    {/* ------ accProfitLoss ------ */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(equity.accProfitLoss)}
                        onChange={(e) =>
                          handleNumericChange({
                            e,
                            index,
                            fieldName: "accProfitLoss",
                            data: initialNotCurrentYearEquity,
                            setData: setInitialNotCurrentYearEquity,
                          })
                        }
                        inputMode="numeric"
                        className="text-right"
                        placeholder="1,000,000"
                        // required
                      />
                    </TableCell>

                    {/* ------ Other ------ */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(equity.other)}
                        onChange={(e) =>
                          handleNumericChange({
                            e,
                            index,
                            fieldName: "other",
                            data: initialNotCurrentYearEquity,
                            setData: setInitialNotCurrentYearEquity,
                          })
                        }
                        inputMode="numeric"
                        className="text-right"
                        placeholder="1,000,000"
                        // required
                      />
                    </TableCell>

                    {/* ------ sum of all four fields ------ */}
                    <TableCell className="text-right border">
                      {computeRowTotal(equity)}
                    </TableCell>

                    {/* ------ remark ------ */}
                    <TableCell className="border">
                      <Input
                        value={equity.remark}
                        onChange={(e) => {
                          const newRows = [...initialNotCurrentYearEquity];
                          newRows[index].remark = e.target.value;
                          setInitialNotCurrentYearEquity(newRows);
                        }}
                        placeholder="ໝາຍເຫດ"
                      />
                    </TableCell>

                    {/* ------ remove row button ------ */}
                    {[0, 1, 2].includes(index) ? null : (
                      <TableCell className="border">
                        <Button
                          onClick={() =>
                            removeEquityRow(
                              index,
                              setInitialNotCurrentYearEquity,
                              initialNotCurrentYearEquity
                            )
                          }
                          type="button"
                          variant={"ghost"}
                          size={"icon"}
                        >
                          <Trash2 className="text-red-500" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {/* ------ Add Row Button ------ */}
                <TableRow className="bg-[#F4F4F5]">
                  <TableCell colSpan={9}>
                    <Button
                      type="button"
                      size="icon"
                      onClick={() =>
                        addNewEquityRow(
                          setInitialNotCurrentYearEquity,
                          initialNotCurrentYearEquity
                        )
                      }
                      className="flex gap-x-2 items-center p-2 my-2 w-auto text-blue-600 border-2 border-blue-600 hover:bg-blue-500 hover:text-white"
                      variant="ghost"
                    >
                      <CirclePlus className="w-4 h-4" />
                      ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                    </Button>
                  </TableCell>
                </TableRow>
                {/* ------ Some fixed rows below ------ */}
                <TableRow className="bg-[#216C4C]/20">
                  <TableCell className="border">
                    {/* II. ປັບປຸງຍອດຄືນໃຫມ່ 1 ມັງກອນ n */}
                    II. ປັບປຸງຍອດຄືນໃຫມ່ 1 ມັງກອນ {year ? year - 1 : ""}
                  </TableCell>
                  <TableCell className="text-right border">
                    {computeSumForField(
                      initialNotCurrentYearEquity,
                      "registeredCapital"
                    )}
                  </TableCell>
                  <TableCell className="text-right border">
                    {computeSumForField(initialNotCurrentYearEquity, "storage")}
                  </TableCell>
                  <TableCell className="text-right border">
                    {computeSumForField(
                      initialNotCurrentYearEquity,
                      "expansion"
                    )}
                  </TableCell>
                  <TableCell className="text-right border">
                    {computeSumForField(
                      initialNotCurrentYearEquity,
                      "accProfitLoss"
                    )}
                  </TableCell>
                  <TableCell className="text-right border">
                    {computeSumForField(initialNotCurrentYearEquity, "other")}
                  </TableCell>
                  <TableCell className="text-right border">
                    {(() => {
                      const total = (
                        [
                          "registeredCapital",
                          "storage",
                          "expansion",
                          "accProfitLoss",
                          "other",
                        ] as const
                      ).reduce(
                        (acc, field) =>
                          acc +
                          parseParenthesisNumber(
                            initialNotCurrentYearEquity.reduce(
                              (sum, row) =>
                                sum + parseParenthesisNumber(row[field]),
                              0
                            )
                          ),
                        0
                      );
                      return formatNumberWithCommas(total);
                    })()}
                  </TableCell>
                  <TableCell className="text-right border">-</TableCell>

                  <TableCell className="text-right border">-</TableCell>
                </TableRow>

                <TableRow className="bg-[#216C4C]/20">
                  <TableCell className="border">
                    {/* III. ສ່ວນປ່ຽນແປງທຶນເຈົ້າຂອງ ໃນປີ n */}
                    III. ສ່ວນປ່ຽນແປງທຶນເຈົ້າຂອງ ໃນປີ {year ? year - 1 : ""}
                  </TableCell>
                  <TableCell className="text-right border">
                    {computeSumForField(
                      initialCurrentYearEquity,
                      "registeredCapital"
                    )}
                  </TableCell>
                  <TableCell className="text-right border">
                    {computeSumForField(initialCurrentYearEquity, "storage")}
                  </TableCell>
                  <TableCell className="text-right border">
                    {computeSumForField(initialCurrentYearEquity, "expansion")}
                  </TableCell>
                  <TableCell className="text-right border">
                    {computeSumForField(
                      initialCurrentYearEquity,
                      "accProfitLoss"
                    )}
                  </TableCell>
                  <TableCell className="text-right border">
                    {computeSumForField(initialCurrentYearEquity, "other")}
                  </TableCell>
                  <TableCell className="text-right border">
                    {/* Compute the total of all fields */}
                    {(() => {
                      const total = (
                        [
                          "registeredCapital",
                          "storage",
                          "expansion",
                          "accProfitLoss",
                          "other",
                        ] as const
                      ).reduce(
                        (acc, field) =>
                          acc +
                          parseParenthesisNumber(
                            initialCurrentYearEquity.reduce(
                              (sum, row) =>
                                sum + parseParenthesisNumber(row[field]),
                              0
                            )
                          ),
                        0
                      );
                      return formatNumberWithCommas(total);
                    })()}
                  </TableCell>
                  <TableCell className="text-right border">-</TableCell>

                  <TableCell className="text-right border">-</TableCell>
                </TableRow>

                {/* Equity Table (Current Year) */}
                {initialCurrentYearEquity?.map((equity, index) => (
                  <TableRow key={index}>
                    {/* ------ name ------ */}
                    <TableCell className="border">
                      {[0, 1, 2, 3, 4, 5].includes(index) ? (
                        <>{equity.name}</>
                      ) : (
                        <Input
                          type="text"
                          value={equity.name}
                          onChange={(e) => {
                            const newRows = [...initialCurrentYearEquity];
                            newRows[index].name = e.target.value;
                            setInitialCurrentYearEquity(newRows);
                          }}
                          required
                        />
                      )}
                    </TableCell>

                    {/* ------ registeredCapital ------ */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(equity.registeredCapital)}
                        onChange={(e) =>
                          handleNumericChange({
                            e,
                            index,
                            fieldName: "registeredCapital",
                            data: initialCurrentYearEquity,
                            setData: setInitialCurrentYearEquity,
                          })
                        }
                        inputMode="numeric"
                        className="text-right"
                        placeholder="1,000,000"
                        // required
                      />
                    </TableCell>

                    {/* ------ storage ------ */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(equity.storage)}
                        onChange={(e) =>
                          handleNumericChange({
                            e,
                            index,
                            fieldName: "storage",
                            data: initialCurrentYearEquity,
                            setData: setInitialCurrentYearEquity,
                          })
                        }
                        inputMode="numeric"
                        className="text-right"
                        placeholder="1,000,000"
                        // required
                      />
                    </TableCell>

                    {/* ------ expansion ------ */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(equity.expansion)}
                        onChange={(e) =>
                          handleNumericChange({
                            e,
                            index,
                            fieldName: "expansion",
                            data: initialCurrentYearEquity,
                            setData: setInitialCurrentYearEquity,
                          })
                        }
                        inputMode="numeric"
                        className="text-right"
                        placeholder="1,000,000"
                        // required
                      />
                    </TableCell>

                    {/* ------ accProfitLoss ------ */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(equity.accProfitLoss)}
                        onChange={(e) =>
                          handleNumericChange({
                            e,
                            index,
                            fieldName: "accProfitLoss",
                            data: initialCurrentYearEquity,
                            setData: setInitialCurrentYearEquity,
                          })
                        }
                        inputMode="numeric"
                        className="text-right"
                        placeholder="1,000,000"
                        // required
                      />
                    </TableCell>

                    {/* ------ other ------ */}
                    <TableCell className="border">
                      <Input
                        type="text"
                        value={formatNumberWithCommas(equity.other)}
                        onChange={(e) =>
                          handleNumericChange({
                            e,
                            index,
                            fieldName: "other",
                            data: initialCurrentYearEquity,
                            setData: setInitialCurrentYearEquity,
                          })
                        }
                        inputMode="numeric"
                        className="text-right"
                        placeholder="1,000,000"
                        // required
                      />
                    </TableCell>

                    {/* ------ sum of all fields ------ */}
                    <TableCell className="text-right border">
                      {computeRowTotal(equity)}
                    </TableCell>

                    {/* ------ remark ------ */}
                    <TableCell className="border">
                      <Input
                        value={equity.remark}
                        onChange={(e) => {
                          const newRows = [...initialCurrentYearEquity];
                          newRows[index].remark = e.target.value;
                          setInitialCurrentYearEquity(newRows);
                        }}
                        placeholder="ໝາຍເຫດ"
                      />
                    </TableCell>

                    {/* ------ Remove row ------ */}
                    {[0, 1, 2, 3, 4, 5].includes(index) ? null : (
                      <TableCell className="border">
                        <Button
                          onClick={() =>
                            removeEquityRow(
                              index,
                              setInitialCurrentYearEquity,
                              initialCurrentYearEquity
                            )
                          }
                          type="button"
                          variant={"ghost"}
                          size={"icon"}
                        >
                          <Trash2 className="text-red-500" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}

                {/* ------ Add Row for Current Year ------ */}
                <TableRow className="bg-[#F4F4F5]">
                  <TableCell colSpan={9}>
                    <Button
                      type="button"
                      size="icon"
                      onClick={() =>
                        addNewEquityRow(
                          setInitialCurrentYearEquity,
                          initialCurrentYearEquity
                        )
                      }
                      className="flex gap-x-2 items-center p-2 my-2 w-auto text-blue-600 border-2 border-blue-600 hover:bg-blue-500 hover:text-white"
                      variant="ghost"
                    >
                      <CirclePlus className="w-4 h-4" />
                      ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                    </Button>
                  </TableCell>
                </TableRow>

                {/* ------ Final row ------ */}
                <TableRow className="bg-[#216C4C]/20">
                  <TableCell className="text-right border">
                    ຍອດຍັງເຫຼືອ 31 ທັນວາ {year ? year - 1 : ""}
                  </TableCell>
                  {/* Sum of total registered capital current year and not current year */}
                  <TableCell className="text-right border">
                    {formatNumberWithCommas(
                      initialNotCurrentYearEquity.reduce(
                        (sum, row) =>
                          sum + parseParenthesisNumber(row.registeredCapital),
                        0
                      ) +
                        initialCurrentYearEquity.reduce(
                          (sum, row) =>
                            sum + parseParenthesisNumber(row.registeredCapital),
                          0
                        )
                    )}
                  </TableCell>

                  {/* Sum of total storage current year and not current year */}
                  <TableCell className="text-right border">
                    {formatNumberWithCommas(
                      initialNotCurrentYearEquity.reduce(
                        (sum, row) => sum + parseParenthesisNumber(row.storage),
                        0
                      ) +
                        initialCurrentYearEquity.reduce(
                          (sum, row) =>
                            sum + parseParenthesisNumber(row.storage),
                          0
                        )
                    )}
                  </TableCell>

                  {/* Sum of total expansion capital current year and not current year */}
                  <TableCell className="text-right border">
                    {formatNumberWithCommas(
                      initialNotCurrentYearEquity.reduce(
                        (sum, row) =>
                          sum + parseParenthesisNumber(row.expansion),
                        0
                      ) +
                        initialCurrentYearEquity.reduce(
                          (sum, row) =>
                            sum + parseParenthesisNumber(row.expansion),
                          0
                        )
                    )}
                  </TableCell>

                  {/* Sum of total accProfitLoss current year and not current year */}
                  <TableCell className="text-right border">
                    {formatNumberWithCommas(
                      initialNotCurrentYearEquity.reduce(
                        (sum, row) =>
                          sum + parseParenthesisNumber(row.accProfitLoss),
                        0
                      ) +
                        initialCurrentYearEquity.reduce(
                          (sum, row) =>
                            sum + parseParenthesisNumber(row.accProfitLoss),
                          0
                        )
                    )}
                  </TableCell>

                  {/* Sum of total other current year and not current year */}
                  <TableCell className="text-right border">
                    {formatNumberWithCommas(
                      initialNotCurrentYearEquity.reduce(
                        (sum, row) => sum + parseParenthesisNumber(row.other),
                        0
                      ) +
                        initialCurrentYearEquity.reduce(
                          (sum, row) => sum + parseParenthesisNumber(row.other),
                          0
                        )
                    )}
                  </TableCell>

                  {/* Sum of all fields */}
                  <TableCell className="text-right border">
                    {formatNumberWithCommas(
                      initialNotCurrentYearEquity.reduce(
                        (sum, row) =>
                          sum +
                          parseParenthesisNumber(row.registeredCapital) +
                          parseParenthesisNumber(row.storage) +
                          parseParenthesisNumber(row.expansion) +
                          parseParenthesisNumber(row.accProfitLoss) +
                          parseParenthesisNumber(row.other),
                        0
                      ) +
                        initialCurrentYearEquity.reduce(
                          (sum, row) =>
                            sum +
                            parseParenthesisNumber(row.registeredCapital) +
                            parseParenthesisNumber(row.storage) +
                            parseParenthesisNumber(row.expansion) +
                            parseParenthesisNumber(row.accProfitLoss) +
                            parseParenthesisNumber(row.other),
                          0
                        )
                    )}
                  </TableCell>

                  <TableCell className="text-right border">-</TableCell>
                  <TableCell className="text-right border">-</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* ------------------- File Upload ------------------- */}
            {/* <div className="flex gap-x-1 items-center mt-4">
							<Label>ເອກະສານແນບ</Label>
							<Input
								type="file"
								onChange={(e) => setDocFile(e.target.value)}
								placeholder="ລິ້ງເອກະສານ"
								className="max-w-[300px]"
							/>
						</div> */}

            <div className="flex gap-x-1 items-center mt-4">
              <FileUpload
                currentFile={docFile}
                onFileUpload={(file) => {
                  console.log(file, "File Uploaded");
                  setDocFile(file);
                }}
              />
            </div>

            {/* ------------------- Action Buttons ------------------- */}
            <div className="flex gap-x-2 justify-end mt-4">
              <Button
                type="button"
                onClick={() =>
                  navigate({
                    to: "/report/finance",
                    search: { page: 1, startYear: 0, endYear: 0 },
                  })
                }
                variant={"outline"}
              >
                ຍົກເລີກ
              </Button>
              <Button
                disabled={mutation.isPending}
                type="submit"
                className="flex gap-x-2 items-center"
              >
                {mutation.isPending ? (
                  <Spinner className="text-white" />
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    ບັນທຶກ
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
